import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Vehicle, VehicleTracker } from 'models/Vehicle';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import { Field, Form } from 'react-final-form';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SelectField from 'components/SelectField';

import './AddVehicleTrackerModal.styles.scss';
import './AddVehicleTrackerModal.styles.responsive.scss';

type AddVehicleTrackerModalProps = {
  className?: string;
  vehicleId: string;
  onSuccess: (vehicle: Vehicle) => void;
};
const VehicleTracerModels = ['telfmb204', 'telfmu130_fmc130_230'];

const TrackerModelCopy = {
  telfmb204: 'Teltonika FMB 204',
  telfmu130_fmc130_230: 'Teltonika FMC 230',
};

const AddVehicleTrackerModal: ForwardRefRenderFunction<
  OverlayRef,
  AddVehicleTrackerModalProps
> = (props, ref) => {
  const { className, vehicleId, onSuccess } = props;
  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { t } = useTranslation();
  const { popError } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('add-vehicle-tracker-modal', className);

  const onAddVehicleTracker = useCallback(
    async (values: Partial<VehicleTracker>) => {
      try {
        const { data } = await api.vehicle.addVehicleTracker(vehicleId, {
          ...values,
          msisdn: +values.msisdn,
        });

        data && onSuccess(data);
      } catch (e: any) {
        popError(e);
      }
    },
    [onSuccess, popError, vehicleId],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <Form
        onSubmit={onAddVehicleTracker}
        render={({ handleSubmit, submitting, pristine }) => (
          <form onSubmit={handleSubmit}>
            <p className="modal-title">{t('addTracker')}</p>
            <Field
              placeholder={t('addTrackerModel')}
              label={t('model')}
              name="model"
              component={SelectField}
              options={VehicleTracerModels.map((opt) => ({
                value: opt,
                label: (TrackerModelCopy as any)[opt],
              }))}
              required
            />
            <Field
              label={t('msisdn')}
              name="msisdn"
              component={InputField}
              type="number"
              required
            />
            <Field
              label={t('imei')}
              name="imei"
              component={InputField}
              required
            />

            <div className="actions">
              <Button aria-label="close" variant="link" onClick={modal.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={pristine || submitting}>
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddVehicleTrackerModal);
